import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  VStack,
  Skeleton,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { Header, MetricProto } from "build-ts/metadata";
import { KGColumnProto } from "build-ts/knowledege_graph";
import React, { useMemo, useState } from "react";
import EntityLeftPanelCard from "./EntityLeftPanelCard";
import { AppIcons } from "constants/icons";

interface DataScopeLeftPanelProps {
  // Metrics
  listOfMetrics: Header[];
  isLoadingMetrics: boolean;
  selectedMetricHeaders: Header[];
  toggleMetricSelection: (metric: Header) => void;
  onAddAllMetrics: () => void;
  onRemoveAllMetrics: () => void;

  // Related entities — separate lists
  attributeList: Header[];
  selectedAttributes: Header[];
  toggleAttributeSelection: (header: Header) => void;

  measureList: Header[];
  selectedMeasures: Header[];
  toggleMeasureSelection: (header: Header) => void;

  timeframeList: Header[];
  selectedTimeframes: Header[];
  toggleTimeframeSelection: (header: Header) => void;

  benchmarkList: Header[];
  selectedBenchmarks: Header[];
  toggleBenchmarkSelection: (header: Header) => void;

  onAddAllRelatedEntities: () => void;
  onRemoveAllRelatedEntities: () => void;

  isLocked?: boolean;
  isLoadingRelatedEntities?: boolean;
  kgColumnProtoMap: Map<string, KGColumnProto>;
  dataModelList: Header[];
  metricProtoMap: Map<string, MetricProto>;
}

export type EntityKind = "metric" | "attribute" | "measure" | "time" | "benchmark";

export const SCROLLBAR_SX = {
  scrollbarWidth: "thin",
  scrollbarColor: "#CBD5E0 transparent",
  "&::-webkit-scrollbar": { width: "6px" },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": { backgroundColor: "#CBD5E0", borderRadius: "6px" },
  "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#A0AEC0" },
};

interface RelatedEntity {
  header: Header;
  kind: EntityKind;
}

export const getKindIcon = (kind: EntityKind) => {
  switch (kind) {
    case "metric": return AppIcons.metric;
    case "attribute": return AppIcons.attribute;
    case "measure": return AppIcons.measure;
    case "time": return AppIcons.timeframe;
    case "benchmark": return AppIcons.benchmark;
    default: return AppIcons.metric;
  }
};

/**
 * For a KG column (measure / attribute / timeframe), traverse its logical_columns
 * and return one [sourceColumnName, modelName] pair per entry.
 * Returns an empty array when the column isn't in the cache yet.
 */
export function getKgColumnTooltipEntries(
  headerId: string,
  kgColumnProtoMap: Map<string, KGColumnProto>,
  dataModelList: Header[],
): Array<[string, string]> {
  const kgCol = kgColumnProtoMap.get(headerId);
  if (!kgCol?.logical_columns?.length) return [];

  return kgCol.logical_columns.map(lc => [
    lc.name ?? "—",
    lc.owner_id ? (dataModelList.find(m => m.id === lc.owner_id)?.name ?? "Unknown model") : "Unknown model",
  ]);
}

export const EntityNameTooltip: React.FC<{
  children: React.ReactNode;
  isDisabled?: boolean;
  /** Use for block row/card triggers; Text-only triggers may need the default. */
  shouldWrapChildren?: boolean;
  header: Header;
  kind: EntityKind;
  dataModelList: Header[];
  metricProtoMap: Map<string, MetricProto>;
  kgColumnProtoMap: Map<string, KGColumnProto>;
}> = ({ children, isDisabled = false, shouldWrapChildren = true, header, kind, dataModelList, metricProtoMap, kgColumnProtoMap }) => {
  // Benchmarks don't have a source column / data model — skip tooltip entirely
  const isBenchmark = kind === "benchmark";
  const isKgColumn = kind === "measure" || kind === "attribute" || kind === "time";

  // ── KG column tooltip: [sourceColumnName, modelName][] via getKgColumnTooltipEntries ──
  const kgColumnEntries = useMemo(() => {
    if (!isKgColumn || !header.id) return [];
    return getKgColumnTooltipEntries(header.id, kgColumnProtoMap, dataModelList);
  }, [isKgColumn, header.id, kgColumnProtoMap, dataModelList]);

  // ── Metric tooltip: source-column + data-model via metricProtoMap ──────────────────
  const { sourceColumnName, modelName } = useMemo(() => {
    if (isBenchmark || isKgColumn || !header.id) return { sourceColumnName: null, modelName: null };

    const metricProto = metricProtoMap.get(header.id);
    const firstCol = metricProto?.definition?.observation_columns?.[0];
    const logicalColHeader = firstCol?.logical_column_header;

    return {
      sourceColumnName: logicalColHeader?.name ?? null,
      modelName: logicalColHeader?.owner_id
        ? (dataModelList.find(m => m.id === logicalColHeader.owner_id)?.name ?? null)
        : null,
    };
  }, [header.id, isBenchmark, isKgColumn, metricProtoMap, dataModelList]);

  const hasData = isKgColumn ? kgColumnEntries.length > 0 : !isBenchmark && (sourceColumnName || modelName);

  return (
    <Tooltip
      isDisabled={isDisabled || !hasData}
      hasArrow
      openDelay={300}
      closeDelay={0}
      placement="top"
      shouldWrapChildren={shouldWrapChildren}
      label={
        isKgColumn ? (
          <Box w="100%" textAlign="left" py={0} px={0}>
            <VStack align="stretch" spacing={1} p={0} w="100%" textAlign="left">
              {kgColumnEntries.map(([col, model], i) => (
                <>
                  <Text key={i} color="white" fontSize="xs" fontWeight="500" lineHeight="1.35" mb={0} textAlign="left" w="100%">
                    Source column: {col}
                  </Text>
                  <Text key={i} color="white" fontSize="xs" fontWeight="500" lineHeight="1.35" mb={0} textAlign="left" w="100%">
                    Data Model: {model}
                  </Text>
                </>
              ))}
            </VStack>
          </Box>
        ) : (
          <Box w="100%" textAlign="left" py={0} px={0}>
            <VStack align="stretch" spacing={1} p={0} w="100%" textAlign="left">
              {sourceColumnName && (
                <Text color="white" fontSize="xs" fontWeight="500" lineHeight="1.35" mb={0} textAlign="left" w="100%">
                  Source column: {sourceColumnName}
                </Text>
              )}
              {modelName && (
                <Text color="white" fontSize="xs" fontWeight="500" lineHeight="1.35" mb={0} textAlign="left" w="100%">
                  Data model: {modelName}
                </Text>
              )}
            </VStack>
          </Box>
        )
      }
      bg="gray.900"
      color="white"
      borderWidth={0}
      px={2.5}
      py={2}
      borderRadius="md"
      arrowSize={6}
      boxShadow="md"
      textAlign="left"
      motionProps={{
        transition: { duration: 0.15, ease: "easeOut" },
      }}
    >
      {children}
    </Tooltip>
  );
};

const DataScopeLeftPanel: React.FC<DataScopeLeftPanelProps> = ({
  listOfMetrics,
  isLoadingMetrics,
  selectedMetricHeaders,
  toggleMetricSelection,
  onAddAllMetrics,
  onRemoveAllMetrics,
  attributeList,
  selectedAttributes,
  toggleAttributeSelection,
  measureList,
  selectedMeasures,
  toggleMeasureSelection,
  timeframeList,
  selectedTimeframes,
  toggleTimeframeSelection,
  benchmarkList,
  selectedBenchmarks,
  toggleBenchmarkSelection,
  onAddAllRelatedEntities,
  onRemoveAllRelatedEntities,
  isLocked = false,
  isLoadingRelatedEntities = false,
  kgColumnProtoMap,
  dataModelList,
  metricProtoMap,
}) => {

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedModelId, setSelectedModelId] = useState<string>("");

  // Set of header IDs (metrics + KG columns) that belong to the selected model.
  // null means no model filter is active.
  const modelFilteredIds = useMemo<Set<string> | null>(() => {
    if (!selectedModelId) return null;
    const ids = new Set<string>();
    metricProtoMap.forEach((metric, metricId) => {
      const cols = metric.definition?.observation_columns ?? [];
      if (cols.some(col => col.logical_column_header?.owner_id === selectedModelId)) {
        ids.add(metricId);
      }
    });
    kgColumnProtoMap.forEach((kgCol, kgColId) => {
      const lcs = kgCol.logical_columns ?? [];
      if (lcs.some(lc => lc.owner_id === selectedModelId)) {
        ids.add(kgColId);
      }
    });
    return ids;
  }, [selectedModelId, metricProtoMap, kgColumnProtoMap]);

  const relatedEntities: RelatedEntity[] = useMemo(() => {
    const all: RelatedEntity[] = [
      ...attributeList.map(h => ({ header: h, kind: "attribute" as EntityKind })),
      ...measureList.map(h => ({ header: h, kind: "measure" as EntityKind })),
      ...timeframeList.map(h => ({ header: h, kind: "time" as EntityKind })),
      ...benchmarkList.map(h => ({ header: h, kind: "benchmark" as EntityKind })),
    ];
    return all;
  }, [attributeList, measureList, timeframeList, benchmarkList]);

  const isRelatedEntitySelected = (header: Header): boolean => {
    const id = header.id;
    if (!id) return false;
    return (
      selectedAttributes.some(h => h.id === id) ||
      selectedMeasures.some(h => h.id === id) ||
      selectedTimeframes.some(h => h.id === id) ||
      selectedBenchmarks.some(h => h.id === id)
    );
  };

  const handleRelatedEntityToggle = (header: Header, kind: EntityKind) => {
    switch (kind) {
      case "benchmark": return toggleBenchmarkSelection(header);
      case "measure": return toggleMeasureSelection(header);
      case "time": return toggleTimeframeSelection(header);
      case "attribute": return toggleAttributeSelection(header);
    }
  };

  const filteredMetrics = useMemo(() => {
    let result = listOfMetrics;
    if (modelFilteredIds !== null) {
      result = result.filter(m => m.id && modelFilteredIds.has(m.id));
    }
    if (searchQuery.trim()) {
      result = result.filter(m => m.name?.toLowerCase().includes(searchQuery.toLowerCase()));
    }
    return result;
  }, [listOfMetrics, searchQuery, modelFilteredIds]);

  const filteredRelatedEntities = useMemo(() => {
    let result = relatedEntities;
    if (modelFilteredIds !== null) {
      result = result.filter(({ header }) => header.id && modelFilteredIds.has(header.id));
    }
    if (searchQuery.trim()) {
      result = result.filter(({ header }) => header.name?.toLowerCase().includes(searchQuery.toLowerCase()));
    }
    return result;
  }, [relatedEntities, searchQuery, modelFilteredIds]);

  return (
    <Flex
      direction="column"
      flex="0 0 35%"
      minW="220px"
      minH={0}
      h="100%"
      maxH="100%"
      borderRightWidth="1px"
      borderColor="gray.400"
    >
      <Flex
        align="center"
        justify="space-between"
        px={4}
        py={2}
        borderBottomWidth="1px"
        borderColor="gray.100"
        flexShrink={0}
        gap={3}
        flexWrap="wrap"
        w="100%"
      >
        {/* Top bar: search */}
        <InputGroup size="sm" flex="1" minW={0} maxW="100%">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" boxSize={3} />
          </InputLeftElement>
          <Input
            placeholder="Search ontology..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            borderRadius="md"
            bg="gray.50"
            _focus={{ bg: "white", borderColor: "purple.400" }}
            fontWeight="500"
            fontSize="sm"
          />
        </InputGroup>
      </Flex>

      {/* Filter by data model */}
      <Box px={4} py={2} flexShrink={0} w="100%" borderBottomWidth="1px" borderColor="gray.100">
        <Menu matchWidth placement="bottom-start">
          <MenuButton
            as={Button}
            w="100%"
            h="32px"
            variant="outline"
            borderColor="gray.200"
            borderRadius="md"
            bg="white"
            px={2}
            _hover={{ borderColor: "gray.300", bg: "gray.50" }}
            _active={{ bg: "gray.50" }}
            _expanded={{ borderColor: "purple.300" }}
          >
            <HStack spacing={1.5} align="center" w="100%" justify="space-between">
              <HStack spacing={1.5} minW={0} flex="1">
                <Icon as={AppIcons.filter} boxSize={3.5} color="purple.500" flexShrink={0} aria-hidden />
                <Text
                  fontSize="sm"
                  fontWeight="500"
                  color={selectedModelId ? "gray.800" : "gray.500"}
                  mb={0}
                  noOfLines={1}
                  textAlign="left"
                >
                  {selectedModelId
                    ? (dataModelList.find(h => h?.id === selectedModelId)?.name ?? "All data models")
                    : "All data models"}
                </Text>
              </HStack>
              <Icon as={AppIcons.chevronDown} boxSize={3} color="gray.400" flexShrink={0} />
            </HStack>
          </MenuButton>
          <MenuList
            zIndex={20}
            minW={0}
            maxH="220px"
            overflowY="auto"
            py={1}
            borderColor="gray.200"
            boxShadow="md"
            sx={{
              scrollbarWidth: "thin",
              scrollbarColor: "#CBD5E0 transparent",
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-thumb": { backgroundColor: "#CBD5E0", borderRadius: "4px" },
            }}
          >
            <MenuItem
              fontSize="sm"
              fontWeight={!selectedModelId ? "600" : "400"}
              color="gray.800"
              onClick={() => setSelectedModelId("")}
              icon={
                !selectedModelId
                  ? <Icon as={AppIcons.check} boxSize={3.5} color="purple.500" />
                  : <Box w="14px" />
              }
              _hover={{ bg: "purple.50" }}
              _focus={{ bg: "purple.50" }}
            >
              All data models
            </MenuItem>
            {dataModelList.map(header => {
              const isSelected = selectedModelId === header?.id;
              return (
                <MenuItem
                  key={header?.id}
                  fontSize="sm"
                  fontWeight={isSelected ? "600" : "400"}
                  color="gray.800"
                  onClick={() => setSelectedModelId(header?.id ?? "")}
                  icon={
                    isSelected
                      ? <Icon as={AppIcons.check} boxSize={3.5} color="purple.500" />
                      : <Box w="14px" />
                  }
                  _hover={{ bg: "purple.50" }}
                  _focus={{ bg: "purple.50" }}
                >
                  {header?.name}
                </MenuItem>
              );
            })}
          </MenuList>
        </Menu>
      </Box>

      {/* Body: two sections each exactly 50% of the available height */}
      <Box flex="1" minH={0} minW={0} display="flex" flexDirection="column" overflow="hidden">
        {/* Top half: Metrics */}
        <Flex
          flex="1"
          minH={0}
          minW={0}
          flexDirection="column"
          overflow="hidden"
          position="relative"
          opacity={1}
          pointerEvents="auto"
          transition="opacity 0.2s"
        >
          <Flex
            flex="1"
            minH={0}
            minW={0}
            flexBasis={0}
            flexDirection="column"
            overflow="hidden"
            bg="white"
          >
            <HStack
              align="center"
              gap={2}
              px={3}
              py={1.5}
              borderBottom="1px solid"
              borderColor="gray.200"
              flexShrink={0}
              bg="gray.50"
              justifyContent="space-between"
            >
              <Text
                fontSize="xs"
                fontWeight="700"
                color="gray.700"
                letterSpacing="0.08em"
                textTransform="uppercase"
                whiteSpace="nowrap"
                mb={0}
              >
                METRICS
              </Text>
              <HStack spacing={2} flexShrink={0}>
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  fontWeight="600"
                  color="purple.600"
                  borderColor="purple.200"
                  isDisabled={listOfMetrics.length === 0 || isLocked}
                  _hover={{ bg: "purple.50", borderColor: "purple.300" }}
                  h="24px"
                  onClick={e => {
                    e.stopPropagation();
                    if (selectedMetricHeaders.length === listOfMetrics.length) {
                      onRemoveAllMetrics();
                    } else {
                      onAddAllMetrics();
                    }
                  }}
                  mb={0}
                >
                  {selectedMetricHeaders.length === listOfMetrics.length ? "Remove all" : "Add all"}
                </Button>
                <Text
                  as="span"
                  fontSize="xs"
                  color="gray.500"
                  fontWeight="500"
                  whiteSpace="nowrap"
                  mb={0}
                >
                  {selectedMetricHeaders.length} / {listOfMetrics.length}
                </Text>
              </HStack>
            </HStack>
            <Box flex="1" minH={0} overflowY="auto" overflowX="hidden" px={0} pb={0} sx={SCROLLBAR_SX}>
              {isLoadingMetrics ? (
                <VStack spacing={0} align="stretch" px={4} py={2}>
                  {[...Array(6)].map((_, i) => (
                    <Skeleton key={i} h="32px" borderRadius="sm" mb="1px" startColor="gray.100" endColor="gray.200" />
                  ))}
                </VStack>
              ) : (
                filteredMetrics.map(metric => (
                  <EntityLeftPanelCard
                    key={metric.id}
                    header={metric}
                    kind="metric"
                    onToggle={() => toggleMetricSelection(metric)}
                    isSelected={selectedMetricHeaders.some(h => h.id === metric.id)}
                    isDisabled={isLocked}
                    dataModelList={dataModelList}
                    metricProtoMap={metricProtoMap}
                    kgColumnProtoMap={kgColumnProtoMap}
                  />
                ))
              )}
            </Box>
          </Flex>
          {/* Bottom half: Related entities */}
          <Flex
            flex="1 1 0px"
            minH={0}
            minW={0}
            flexDirection="column"
            overflow="hidden"
            bg="white"
          >
            <HStack
              align="center"
              gap={2}
              px={3}
              py={1.5}
              borderBottom="1px solid"
              borderTop="1px solid"
              borderColor="gray.300"
              flexShrink={0}
              bg="gray.50"
              justifyContent="space-between"
            >
              <Text
                fontSize="xs"
                fontWeight="700"
                color="gray.700"
                letterSpacing="0.08em"
                textTransform="uppercase"
                whiteSpace="nowrap"
                mb={0}
              >
                RELATED ENTITIES
              </Text>
              <HStack spacing={2} flexShrink={0}>
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  fontWeight="600"
                  color="purple.600"
                  borderColor="purple.200"
                  isDisabled={isLocked || selectedMetricHeaders.length === 0 || relatedEntities.length === 0}
                  _hover={{ bg: "purple.50", borderColor: "purple.300" }}
                  h="24px"
                  onClick={e => {
                    e.stopPropagation();
                    if (selectedAttributes.length
                      + selectedMeasures.length
                      + selectedTimeframes.length
                      + selectedBenchmarks.length === relatedEntities.length) {
                      onRemoveAllRelatedEntities();
                    } else {
                      onAddAllRelatedEntities();
                    }
                  }}
                  mb={0}
                >
                  {selectedAttributes.length
                    + selectedMeasures.length
                    + selectedTimeframes.length
                    + selectedBenchmarks.length === relatedEntities.length ? "Remove all" : "Add all"}
                </Button>
                <Text
                  as="span"
                  fontSize="xs"
                  color="gray.500"
                  fontWeight="500"
                  whiteSpace="nowrap"
                  mb={0}
                >
                  {selectedAttributes.length
                    + selectedMeasures.length
                    + selectedTimeframes.length
                    + selectedBenchmarks.length}
                  / {relatedEntities.length}
                </Text>
              </HStack>
            </HStack>
            <Box
              flex="1"
              minH={0}
              overflowY="auto"
              overflowX="hidden"
              px={0}
              pb={0}
              sx={SCROLLBAR_SX}
            >
              {isLoadingRelatedEntities ? (
                <VStack spacing={0} align="stretch" px={4} py={2}>
                  {[...Array(6)].map((_, i) => (
                    <Skeleton key={i} h="32px" borderRadius="sm" mb="1px" startColor="gray.100" endColor="gray.200" />
                  ))}
                </VStack>
              ) : selectedMetricHeaders.length === 0 ? (
                <Flex minH="100px" align="center" justify="center" px={4} py={6}>
                  <Text
                    fontSize="sm"
                    color="gray.500"
                    textAlign="center"
                    mb={0}
                    lineHeight="1.5"
                    maxW="240px"
                  >
                    Select a metric above to see related entities.
                  </Text>
                </Flex>
              ) : (
                !isLoadingMetrics &&
                filteredRelatedEntities.map(r => (
                  <EntityLeftPanelCard
                    key={r.header.id}
                    header={r.header}
                    kind={r.kind}
                    onToggle={() => handleRelatedEntityToggle(r.header, r.kind)}
                    isSelected={isRelatedEntitySelected(r.header)}
                    isDisabled={isLocked}
                    dataModelList={dataModelList}
                    metricProtoMap={metricProtoMap}
                    kgColumnProtoMap={kgColumnProtoMap}
                  />
                ))
              )}
            </Box>
          </Flex>
        </Flex>
      </Box>
    </Flex>
  );
};

export default DataScopeLeftPanel;
